import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import { getMediaUrl } from "../utils/mediaUtils";
import {
  formatFileSize,
  getDocumentIcon,
  getDocumentDownloadUrl,
} from "../utils/documentUtils";
import SharedMediaViewer from "./SharedMediaViewer";
import VoiceMessage from "./VoiceMessage";

const TABS = [
  { id: "media", label: "Media" },
  { id: "links", label: "Links" },
  { id: "docs", label: "Docs" },
];

function ProfileSharedContent({
  user,
  contactUserId,
  initialTab = "media",
  embedded = false,
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [data, setData] = useState({ media: [], links: [], docs: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mediaViewer, setMediaViewer] = useState({
    isOpen: false,
    type: "image",
    url: "",
  });

  const openMediaViewer = (type, url) => {
    setMediaViewer({ isOpen: true, type, url });
  };

  const closeMediaViewer = () => {
    setMediaViewer({ isOpen: false, type: "image", url: "" });
  };

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, contactUserId]);

  useEffect(() => {
    if (!user?.id || !contactUserId) return;
    fetchShared();
  }, [user?.id, contactUserId]);

  const fetchShared = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(
        `${config.apiUrl}/api/messages/shared/${user.id}/${contactUserId}`,
        { params: { requestingUserId: user.id } }
      );
      setData({
        media: res.data.media || [],
        links: res.data.links || [],
        docs: res.data.docs || [],
        counts: res.data.counts || {},
      });
    } catch (err) {
      console.error("Error loading shared content:", err);
      setError(err.response?.data?.error || "Failed to load shared content");
    } finally {
      setLoading(false);
    }
  };

  const formatSharedDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
    });
  };

  const counts = data.counts || {
    media: data.media.length,
    links: data.links.length,
    docs: data.docs.length,
  };

  const renderEmpty = (label) => (
    <div className="profile-shared-empty">
      <p>No shared {label} yet</p>
    </div>
  );

  const renderMedia = () => {
    if (!data.media.length) return renderEmpty("media");
    return (
      <div className="profile-shared-media-grid">
        {data.media.map((item) => {
          const mediaUrl = getMediaUrl(item.url);
          if (item.kind === "video") {
            return (
              <button
                key={item.id}
                type="button"
                className="profile-shared-media-item video-tile"
                onClick={() => openMediaViewer("video", mediaUrl)}
                aria-label="Play video"
              >
                <video
                  src={mediaUrl}
                  className="shared-media-thumb"
                  preload="metadata"
                  muted
                  playsInline
                />
                <span className="profile-shared-media-play-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="white">
                    <polygon points="8,5 19,12 8,19" />
                  </svg>
                </span>
                <span className="profile-shared-badge">
                  {item.isFromMe ? "You" : "Them"}
                </span>
              </button>
            );
          }
          if (item.kind === "voice") {
            return (
              <div key={item.id} className="profile-shared-media-item voice">
                <VoiceMessage
                  message={{
                    _id: item.id,
                    voiceUrl: item.url,
                    voiceDuration: item.voiceDuration,
                  }}
                  isMe={item.isFromMe}
                />
                <span className="profile-shared-date">{formatSharedDate(item.createdAt)}</span>
              </div>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              className="profile-shared-media-item image"
              onClick={() => openMediaViewer("image", mediaUrl)}
            >
              <img src={mediaUrl} alt="" loading="lazy" />
              <span className="profile-shared-badge">
                {item.isFromMe ? "You" : "Them"}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderLinks = () => {
    if (!data.links.length) return renderEmpty("links");
    return (
      <ul className="profile-shared-links-list">
        {data.links.map((item) => (
          <li key={item.id}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-shared-link-card"
            >
              <span className="profile-shared-link-url">{item.url}</span>
              {item.previewText && (
                <span className="profile-shared-link-preview">{item.previewText}</span>
              )}
              <span className="profile-shared-link-meta">
                {item.isFromMe ? "Shared by you" : "Shared by contact"} ·{" "}
                {formatSharedDate(item.createdAt)}
              </span>
            </a>
          </li>
        ))}
      </ul>
    );
  };

  const renderDocs = () => {
    if (!data.docs.length) return renderEmpty("documents");
    return (
      <ul className="profile-shared-docs-list">
        {data.docs.map((item) => (
          <li key={item.id}>
            <a
              href={getDocumentDownloadUrl(item.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-shared-doc-card"
              download={item.name}
            >
              <span className="profile-shared-doc-icon">
                {getDocumentIcon(item.fileType || item.name?.split(".").pop()?.toUpperCase())}
              </span>
              <span className="profile-shared-doc-info">
                <span className="profile-shared-doc-name">{item.name}</span>
                <span className="profile-shared-doc-meta">
                  {item.size ? formatFileSize(item.size) : item.kind}
                  {" · "}
                  {item.isFromMe ? "You" : "Contact"} · {formatSharedDate(item.createdAt)}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={`profile-shared-section${embedded ? " profile-shared-section--embedded" : ""}`}>
      {!embedded && <h4 className="section-title">Shared content</h4>}
      <div className="profile-shared-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`profile-shared-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span className="profile-shared-tab-count">
              {counts[tab.id] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="profile-shared-loading">
          <div className="loading-spinner" />
          <p>Loading…</p>
        </div>
      )}

      {!loading && error && (
        <div className="profile-shared-error">
          <p>{error}</p>
          <button type="button" onClick={fetchShared} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="profile-shared-panel">
          {activeTab === "media" && renderMedia()}
          {activeTab === "links" && renderLinks()}
          {activeTab === "docs" && renderDocs()}
        </div>
      )}

      <SharedMediaViewer
        isOpen={mediaViewer.isOpen}
        type={mediaViewer.type}
        url={mediaViewer.url}
        onClose={closeMediaViewer}
      />
    </div>
  );
}

export default ProfileSharedContent;
